import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, X } from 'lucide-react';
import { useDistinctTags, useSetTagsMutation } from '../../hooks/useAdAnnotations';

const MAX_TAGS = 20;

interface TagsCellProps {
  accountId: string;
  adId: string;
  tags: string[];
}

export default function TagsCell({ accountId, adId, tags }: TagsCellProps) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string[]>(tags);
  const [input, setInput] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { data: allTags } = useDistinctTags();
  const setTagsMut = useSetTagsMutation();

  // Sync draft when external tags change
  useEffect(() => {
    if (!editing) setDraft(tags);
  }, [tags, editing]);

  // Click-outside to commit & close
  useEffect(() => {
    if (!editing) return;
    function onDocClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        commit();
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [editing, draft]);

  function commit() {
    const same =
      draft.length === tags.length && draft.every((t, i) => t === tags[i]);
    if (!same) {
      setTagsMut.mutate({ accountId, adId, tags: draft });
    }
    setEditing(false);
    setInput('');
  }

  function addTag(raw: string) {
    const t = raw.trim().slice(0, 40);
    if (!t) return;
    if (draft.length >= MAX_TAGS) return;
    if (draft.some((x) => x.toLowerCase() === t.toLowerCase())) return;
    setDraft((d) => [...d, t]);
    setInput('');
  }

  function removeTag(idx: number) {
    setDraft((d) => d.filter((_, i) => i !== idx));
  }

  const suggestions = useMemo(() => {
    const q = input.trim().toLowerCase();
    const used = new Set(draft.map((t) => t.toLowerCase()));
    return (allTags ?? [])
      .filter((t) => !used.has(t.toLowerCase()))
      .filter((t) => (q ? t.toLowerCase().includes(q) : true))
      .slice(0, 10);
  }, [allTags, input, draft]);

  if (!editing) {
    return (
      <div
        className="flex flex-wrap items-center gap-1 cursor-text min-h-[24px]"
        onClick={() => {
          setEditing(true);
          setDraft(tags);
          setTimeout(() => inputRef.current?.focus(), 0);
        }}
      >
        {tags.length === 0 ? (
          <span className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600">
            <Plus className="h-3 w-3" />
            {t('insights.addTag')}
          </span>
        ) : (
          tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-700"
            >
              {tag}
            </span>
          ))
        )}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="relative flex flex-wrap items-center gap-1 rounded-md border border-primary/50 bg-white p-1"
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          setEditing(false);
          setDraft(tags);
          setInput('');
        }
      }}
    >
      {draft.map((tag, idx) => (
        <span
          key={`${tag}-${idx}`}
          className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-700"
        >
          {tag}
          <button
            type="button"
            onClick={() => removeTag(idx)}
            className="rounded-full text-blue-500 hover:bg-blue-100 hover:text-blue-700"
            aria-label="remove tag"
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            addTag(input);
          } else if (e.key === 'Backspace' && !input && draft.length) {
            removeTag(draft.length - 1);
          }
        }}
        placeholder={draft.length >= MAX_TAGS ? t('insights.tagsMax', { max: MAX_TAGS }) : t('insights.addTag')}
        disabled={draft.length >= MAX_TAGS}
        className="min-w-[80px] flex-1 bg-transparent text-xs outline-none disabled:cursor-not-allowed"
      />
      {(input.trim() || suggestions.length > 0) && draft.length < MAX_TAGS && (
        <div className="absolute left-0 right-0 top-full z-10 mt-1 max-h-48 overflow-auto rounded-md border bg-white shadow-lg">
          {suggestions.map((s) => (
            <button
              key={s}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                addTag(s);
              }}
              className="block w-full px-3 py-1.5 text-start text-xs hover:bg-gray-50"
            >
              {s}
            </button>
          ))}
          {input.trim() &&
            !suggestions.some((s) => s.toLowerCase() === input.trim().toLowerCase()) && (
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  addTag(input);
                }}
                className="block w-full border-t px-3 py-1.5 text-start text-xs text-primary hover:bg-gray-50"
              >
                {t('insights.newTag', { value: input.trim() })}
              </button>
            )}
        </div>
      )}
    </div>
  );
}
